import { useInRecordSection, useSectionWideClass } from 'components/section-edit/SectionEdit';
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupTextarea } from 'components/ui/input-group';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import React, { InputHTMLAttributes, KeyboardEventHandler, useState } from 'react';
import { Controller, FieldValues } from 'react-hook-form';
import { cn } from 'utils/cn';
import ReadonlyField from '../rhf-other/ReadonlyField';
import { CommonInputProps } from './InputTypes';
import InputWrapper from './InputWrapper';
import { SECTION_TEXT_AREA_CLASS } from './TextAreaInput';
import { useOwnPlaceholder } from './newDesignInput';
import { useMemoizedRules } from './useMemoizedRules';

export interface TextInputProps<T extends FieldValues> extends CommonInputProps<T> {
    suffixComponent?: React.ReactNode;
    onBlur?: (value?: string) => void;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    transformEventBeforeChange?: (e: React.ChangeEvent<HTMLInputElement>) => React.ChangeEvent<HTMLInputElement> | null;
    maxLength?: number;
    pattern?: string | RegExp;
    readonly?: boolean;
    focused?: boolean;
    autoFocus?: boolean;
    readOnlyField?: JSX.Element;
    multiline?: boolean;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    decimalScale?: number;
    inputProps?: InputHTMLAttributes<HTMLInputElement>;
    noReadOnlyDivider?: boolean;
    autoComplete?: 'on' | 'off';
    onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
    /** Opt-in: how the value reads while the field is not focused, an amount with its separators; typing sees it raw. */
    formatValue?: (value: string) => string;
}

const TextInput = <T extends FieldValues>({
    name,
    disabled,
    tooltip,
    required,
    label,
    gap,
    shouldTranslateLabel,
    rightLabelComponent,
    bottomRightLabelComponent,
    placeholder,
    shouldTranslatePlaceholder = true,
    validate,
    control,
    suffixComponent,
    onBlur,
    onChange,
    onMouseEnter,
    onMouseLeave,
    transformEventBeforeChange = (e: React.ChangeEvent<HTMLInputElement>) => e,
    maxLength = undefined,
    pattern,
    readonly = false,
    autoFocus = false,
    readOnlyField,
    multiline,
    wrapperClassName,
    decimalScale,
    inputProps,
    noReadOnlyDivider = false,
    noFullWidthInputWrapper = false,
    autoComplete = 'on',
    onKeyDown,
    formatValue,
}: TextInputProps<T>) => {
    const [isFocused, setIsFocused] = useState(false);
    const rules = useMemoizedRules({ required, validate });
    const ownPlaceholder = useOwnPlaceholder(placeholder, label, shouldTranslatePlaceholder);
    const regExp = pattern ? new RegExp(pattern) : null;
    // In a record section a multiline field takes a text area's span and starts at one line, as a text area does.
    const isSectionTextArea = useInRecordSection() && multiline;
    const wideClass = useSectionWideClass(control, name);

    // The control declares its own onFocus/onKeyDown/onChange/onBlur, so a handler of the same name
    // arriving through `inputProps` has to be called from it rather than spread over it. The order
    // is the one callers already relied on: the supplied onChange runs before the field is written,
    // the other three after the local behaviour.
    const {
        onFocus: suppliedOnFocus,
        onKeyDown: suppliedOnKeyDown,
        onChange: suppliedOnChange,
        onBlur: suppliedOnBlur,
        ...restInputProps
    } = inputProps ?? {};

    const isDeleteAction = (nativeEvent: Event) => {
        return nativeEvent instanceof InputEvent && nativeEvent.inputType?.startsWith('deleteContent');
    };

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({
                field: { ref, value, onChange: innerOnChange, onBlur: innerOnBlur, ...field },
                fieldState: { error },
            }) => {
                const fieldName = 'field_' + name; // just adding anything into the field name to give chromium browsers less clue for guessing input
                const isEmpty = value === undefined || value === null || value === '';
                // The control's own props, shared by the bare and the suffixed rendering.
                const controlProps = {
                    ...field,
                    ref,
                    id: fieldName,
                    disabled,
                    required,
                    maxLength,
                    autoComplete,
                    autoFocus,
                    'aria-invalid': !!error,
                    'data-form-type': 'other',
                    'data-lpignore': 'true',
                    ...restInputProps,
                    value:
                        value === undefined || value === null
                            ? ''
                            : formatValue && !isFocused
                              ? formatValue(String(value))
                              : value,
                    placeholder: ownPlaceholder,
                    onMouseEnter,
                    onMouseLeave,
                    // Sending the caret to the end of the text, the way an autofocused field should open.
                    onFocus: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                        setIsFocused(true);
                        if (formatValue && !isEmpty) {
                            // The raw text goes in now, under the selection: the re-render would only move the caret.
                            event.target.value = String(value);
                            event.target.select();
                        }
                        if (autoFocus) {
                            const temp_value = event.target.value;
                            event.target.value = '';
                            event.target.value = temp_value;
                        }
                        suppliedOnFocus?.(event as React.FocusEvent<HTMLInputElement>);
                    },
                    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                        (onKeyDown as KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined)?.(
                            event
                        );
                        suppliedOnKeyDown?.(event as React.KeyboardEvent<HTMLInputElement>);
                    },
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        suppliedOnChange?.(e);
                        const transformed = transformEventBeforeChange(e);
                        if (transformed === null) {
                            // Block the change - transformEventBeforeChange rejected it
                            return;
                        }
                        if (!regExp) {
                            innerOnChange(transformed);
                            if (onChange) {
                                onChange(e);
                            }
                        } else if (regExp.test(e.target.value) || isDeleteAction(e.nativeEvent)) {
                            innerOnChange(transformed);
                            if (onChange) {
                                onChange(e);
                            }
                        } else if (value === undefined || value === null) {
                            e.target.value = '';
                        }
                    },
                    onBlur: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                        setIsFocused(false);
                        const val = value ? value.toString() : '';
                        innerOnBlur();
                        innerOnChange(val?.trim());
                        if (onBlur) {
                            onBlur(val);
                        }
                        suppliedOnBlur?.(event as React.FocusEvent<HTMLInputElement>);
                    },
                };

                // `multiline` swaps the control for a textarea; the props are the same set either way,
                // and only the element type the handlers are declared against differs.
                const textareaProps = controlProps as unknown as React.ComponentProps<typeof Textarea>;

                const renderControl = () => {
                    if (suffixComponent) {
                        return (
                            <InputGroup className="bg-paper">
                                {multiline ? (
                                    <InputGroupTextarea {...textareaProps} />
                                ) : (
                                    <InputGroupInput {...controlProps} />
                                )}
                                {/* On touch a suffix's 24px buttons, HoursInput's steppers, get 44px areas that meet. */}
                                <InputGroupAddon align="inline-end" className="pointer-coarse:gap-5">
                                    {suffixComponent}
                                </InputGroupAddon>
                            </InputGroup>
                        );
                    }
                    // Only the bare control paints the paper background. Inside the group the group paints it,
                    // and a white input (h-9 in a bordered h-9 group) would cover the group's top and bottom border.
                    return multiline ? (
                        <Textarea
                            {...textareaProps}
                            className={cn('bg-paper', isSectionTextArea && SECTION_TEXT_AREA_CLASS)}
                        />
                    ) : (
                        <Input {...controlProps} className="bg-paper" />
                    );
                };

                return (
                    <InputWrapper
                        name={fieldName}
                        label={label}
                        shouldTranslateLabel={shouldTranslateLabel}
                        tooltip={tooltip}
                        required={required}
                        isEmpty={isEmpty}
                        readonly={!!readonly}
                        rightLabelComponent={rightLabelComponent}
                        bottomRightLabelComponent={bottomRightLabelComponent}
                        hasErrors={!!error}
                        error={readonly ? undefined : error}
                        className={cn(wrapperClassName, isSectionTextArea && wideClass)}
                        gap={gap}
                        noReadOnlyDivider={noReadOnlyDivider}
                        noFullWidthInputWrapper={noFullWidthInputWrapper}
                    >
                        {readonly
                            ? readOnlyField || <ReadonlyField value={value} decimalScale={decimalScale} />
                            : renderControl()}
                    </InputWrapper>
                );
            }}
        />
    );
};

export default TextInput;
